import { useState, useRef } from "react";
import { api, formatApiError } from "../../lib/api";
import { Upload, FileText, ExternalLink, Eye, EyeOff } from "lucide-react";

/**
 * Type-aware editor input for a single page-content field.
 * Props: {
 *   field: {key, value, visible, default, type, label, updated_at, updated_by},
 *   onChange: (newValue) => void,
 *   onToggleVisible: (next: bool) => void,
 * }
 */
export function FieldInput({ field, onChange, onToggleVisible }) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const inputRef = useRef(null);
  const hidden = !field.visible;

  const onUpload = async (file) => {
    if (!file) return;
    setUploadError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch (err) {
      setUploadError(formatApiError(err.response?.data?.detail) || "Échec de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const baseInputCls = `w-full border-2 border-[#1A1A18] bg-[#FAF8F3] px-3 py-3 font-body text-base focus:outline-none focus:border-[#4A7A3D] ${hidden ? "opacity-40 pointer-events-none" : ""}`;

  const renderInput = () => {
    switch (field.type) {
      case "text":
        return (
          <input type="text" value={field.value ?? ""} onChange={(e) => onChange(e.target.value)} className={baseInputCls} data-testid={`field-${field.key}`} disabled={hidden} />
        );
      case "longtext":
        return (
          <textarea rows={4} value={field.value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${baseInputCls} font-mono whitespace-pre-wrap`} data-testid={`field-${field.key}`} disabled={hidden} />
        );
      case "markdown":
        return (
          <div>
            <textarea rows={6} value={field.value ?? ""} onChange={(e) => onChange(e.target.value)} className={`${baseInputCls} font-mono whitespace-pre-wrap`} data-testid={`field-${field.key}`} disabled={hidden} />
            <p className="mt-1 font-mono-label text-[10px] text-[#4B4B48]">
              Markdown · **gras** · *italique* · listes (- item) · liens ([texte](url)) · deux retours ligne = paragraphe
            </p>
          </div>
        );
      case "url":
        return (
          <input type="url" value={field.value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="https://…" className={baseInputCls} data-testid={`field-${field.key}`} disabled={hidden} />
        );
      case "email":
        return (
          <input type="email" value={field.value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="email@exemple.mu" className={baseInputCls} data-testid={`field-${field.key}`} disabled={hidden} />
        );
      case "phone":
        return (
          <input type="tel" value={field.value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="+230 …" className={baseInputCls} data-testid={`field-${field.key}`} disabled={hidden} />
        );
      case "image":
        return (
          <div className={`space-y-3 ${hidden ? "opacity-40 pointer-events-none" : ""}`}>
            {field.value && (
              <div className="border-2 border-[#E0D8CE] bg-[#F5EFEB] p-3 inline-block">
                <img src={field.value} alt="" className="max-w-full max-h-40 object-contain" />
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <input type="url" value={field.value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="URL de l'image (https://…)" className="w-full border-2 border-[#1A1A18] bg-[#FAF8F3] px-3 py-3 font-body text-base flex-1 focus:outline-none focus:border-[#4A7A3D]" data-testid={`field-${field.key}`} disabled={hidden} />
              <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading || hidden} className="min-h-[44px] bg-[#1A1A18] text-[#FAF8F3] px-4 py-2 font-mono-label hover:bg-[#4A7A3D] flex items-center gap-2 disabled:opacity-50" data-testid={`field-${field.key}-upload`}>
                <Upload size={14} /> {uploading ? "Envoi…" : "Téléverser"}
              </button>
              <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml" onChange={(e) => onUpload(e.target.files?.[0])} className="hidden" />
            </div>
            {uploadError && <p className="text-xs text-red-700">{uploadError}</p>}
          </div>
        );
      case "pdf":
        return (
          <div className={`space-y-3 ${hidden ? "opacity-40 pointer-events-none" : ""}`}>
            {field.value && (
              <a href={field.value} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-mono-label text-[#4A7A3D] hover:underline">
                <FileText size={14} /> Voir le PDF actuel <ExternalLink size={12} />
              </a>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <input type="url" value={field.value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="URL du PDF" className="w-full border-2 border-[#1A1A18] bg-[#FAF8F3] px-3 py-3 font-body text-base flex-1 focus:outline-none focus:border-[#4A7A3D]" data-testid={`field-${field.key}`} disabled={hidden} />
              <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading || hidden} className="min-h-[44px] bg-[#1A1A18] text-[#FAF8F3] px-4 py-2 font-mono-label hover:bg-[#4A7A3D] flex items-center gap-2 disabled:opacity-50" data-testid={`field-${field.key}-upload`}>
                <Upload size={14} /> {uploading ? "Envoi…" : "Téléverser"}
              </button>
              <input ref={inputRef} type="file" accept="application/pdf" onChange={(e) => onUpload(e.target.files?.[0])} className="hidden" />
            </div>
            {uploadError && <p className="text-xs text-red-700">{uploadError}</p>}
          </div>
        );
      default:
        return <input type="text" value={field.value ?? ""} onChange={(e) => onChange(e.target.value)} className={baseInputCls} data-testid={`field-${field.key}`} disabled={hidden} />;
    }
  };

  return (
    <div>
      {/* Visibility toggle bar */}
      <div className="flex items-center justify-between gap-3 mb-2">
        <button
          type="button"
          onClick={() => onToggleVisible(!field.visible)}
          className={`min-h-[36px] font-mono-label text-[11px] px-3 py-1.5 border inline-flex items-center gap-2 transition-colors ${
            field.visible
              ? "border-[#4A7A3D] text-[#4A7A3D] hover:bg-[#4A7A3D] hover:text-[#FAF8F3]"
              : "border-[#4B4B48] bg-[#F5EFEB] text-[#4B4B48] hover:bg-[#C4944A] hover:text-[#1A1A18] hover:border-[#C4944A]"
          }`}
          data-testid={`field-${field.key}-toggle-visible`}
          aria-pressed={field.visible}
        >
          {field.visible ? <Eye size={12} /> : <EyeOff size={12} />}
          {field.visible ? "Affiché sur la page" : "Masqué sur la page"}
        </button>
      </div>
      {renderInput()}
    </div>
  );
}
